import React, { useState } from 'react';
import { Bot, LineChart, CreditCard as Edit, MoreVertical, Trash2, CreditCard, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { getStrategyLabel } from '@/utils/strategies';
import { formatPct } from '@/utils/format';
import type { Bot as BotType } from '@/utils/types';
import { formatLocalTime } from '@/utils/backtests';
import { cn } from '../utils/cn';
import { formatValue } from '../utils/formatValue';
import { useBots } from '@/contexts/BotsContext';
import { useAuth } from '@/contexts/AuthContext';
import { cloneBotV2 } from '@/lib/db/bots';
import { toast } from 'sonner';

const gradients = [
  'from-orange-500 via-red-500 to-pink-500',
  'from-red-500 via-pink-500 to-rose-500',
  'from-rose-500 via-red-500 to-orange-500',
  'from-amber-400 via-orange-500 to-red-600',
  'from-yellow-400 via-amber-500 to-orange-600',
  'from-blue-500 via-indigo-500 to-violet-500',
  'from-indigo-500 via-purple-500 to-blue-500',
  'from-violet-500 via-indigo-500 to-blue-500',
  'from-cyan-500 via-blue-500 to-indigo-500',
  'from-sky-400 via-blue-500 to-indigo-600',
  'from-green-400 via-emerald-500 to-teal-500',
  'from-emerald-500 via-green-500 to-lime-500',
  'from-teal-400 via-emerald-500 to-green-600',
  'from-lime-400 via-green-500 to-emerald-500',
  'from-green-500 via-teal-500 to-cyan-500',
  'from-purple-500 via-pink-500 to-rose-500',
  'from-fuchsia-500 via-purple-500 to-pink-500',
  'from-purple-500 via-violet-500 to-indigo-500',
  'from-violet-400 via-purple-500 to-fuchsia-600',
  'from-indigo-400 via-violet-500 to-purple-600',
  'from-yellow-400 via-orange-500 to-red-500',
  'from-pink-500 via-rose-500 to-orange-500',
  'from-blue-500 via-cyan-500 to-teal-500',
  'from-teal-400 via-cyan-500 to-blue-500',
  'from-indigo-500 via-blue-500 to-sky-500',
  'from-amber-400 via-pink-500 to-purple-600',
  'from-blue-400 via-purple-500 to-pink-600',
  'from-green-400 via-teal-500 to-blue-600',
  'from-purple-400 via-indigo-500 to-blue-600',
  'from-rose-400 via-pink-500 to-purple-600'
];

interface BotCardProps {
  bot: BotType;
  onClick?: () => void;
  onDelete?: () => void;
  index?: number;
  onSelect?: (name: string, description: string) => void;
  isSelected?: boolean;
  variant?: 'my-bots' | 'backtests';
}

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  botName: string;
  isDeleting?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  botName,
  isDeleting = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Delete Bot
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This action cannot be undone
            </p>
          </div>
        </div>
        <div className="space-y-3 mb-6">
          <p className="text-gray-600 dark:text-gray-300">
            Are you sure you want to delete <strong>"{botName}"</strong>?
          </p>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-2">
              This will permanently delete:
            </p>
            <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 ml-4">
              <li>• The bot and all its settings</li>
              <li>• All backtests from this bot</li>
              <li>• All trading statistics and history</li>
              <li>• All open positions will be closed</li>
              <li>• All pending orders will be canceled</li>
            </ul>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting...' : 'Delete Everything'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export const BotCard: React.FC<BotCardProps> = ({ bot, onClick, onDelete, index = 0, onSelect, isSelected, variant = 'my-bots' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { deleteBot, toggleBotStatus, toggleBotRunning, refreshBots } = useBots();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // ✅ SINGLE VARIABLE DECLARATIONS BLOCK - KEEP ONLY ONCE
  const isMyBots = variant === 'my-bots';
  const strategyLabel = getStrategyLabel(bot.strategy, bot.strategy_key);
  const profitPct = bot.profit ?? bot.total_profit_pct ?? bot.profit_pct ?? 0;
  const isFromBacktest = bot.created_from_backtest_id || bot.createdFromBacktestId;
  
  const getGradient = () => {
    const botIdHash = bot.id ? hashString(bot.id) : index;
    return gradients[botIdHash % gradients.length];
  };

  const handleEdit = () => {
    if (onClick) {
      onClick();
    }
    navigate('/create-bot');
  };

  const handleSelect = () => {
    if (onSelect && bot.name && bot.description) {
      onSelect(bot.name, bot.description);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    // ✅ Guard: prevent double-click
    if (isDeleting) {
      console.log('[BOT CARD] Delete already in progress, ignoring click');
      return;
    }

    setIsDeleting(true);
    try {
      if (onDelete) {
        // Use provided callback (from MyBotsPage)
        await onDelete();
      } else {
        // ✅ Use HARD delete - removes bot + backtests + statistics + trades
        await deleteBot(bot.id, { hard: true });
        toast.success('Bot and all related data deleted successfully');
      }
      setShowDeleteModal(false);
    } catch (error: any) {
      console.error('[BOT CARD] Delete error:', error);
      // Error toast already shown in BotsContext.deleteBot
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleActive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('[BOT CARD] Toggle clicked', { botId: bot.id, currentIsActive: bot.is_active });

    // ✅ Show confirmation modal if deactivating
    if (bot.is_active) {
      console.log('[BOT CARD] Showing deactivate modal');
      setShowDeactivateModal(true);
      return;
    }

    // ✅ Activate without confirmation
    console.log('[BOT CARD] Activating bot...');
    setIsToggling(true);
    try {
      await toggleBotStatus(bot.id, true);
      console.log('[BOT CARD] Bot activated successfully');
      toast.success('Bot activated');
    } catch (error: any) {
      console.error('[BOT CARD] Toggle error:', error);
      toast.error(`Failed to activate bot: ${error.message || 'Unknown error'}`);
    } finally {
      setIsToggling(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    setShowDeactivateModal(false);
    setIsToggling(true);
    try {
      await toggleBotStatus(bot.id, false);
      toast.success('Bot deactivated');
    } catch (error: any) {
      console.error('[BOT CARD] Toggle error:', error);
      toast.error(`Failed to deactivate bot: ${error.message || 'Unknown error'}`);
    } finally {
      setIsToggling(false);
    }
  };

  const handleToggleRunning = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('[BOT CARD] Toggle running clicked', { botId: bot.id, currentIsRunning: bot.is_running });

    setIsToggling(true);
    try {
      const newRunning = !bot.is_running;
      await toggleBotRunning(bot.id, newRunning);
      toast.success(newRunning ? 'Bot started trading' : 'Bot paused');
    } catch (error: any) {
      console.error('[BOT CARD] Toggle running error:', error);
      toast.error(`Failed to ${bot.is_running ? 'pause' : 'start'} bot: ${error.message || 'Unknown error'}`);
    } finally {
      setIsToggling(false);
    }
  };

  const handleDuplicate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(false);

    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    console.log('[BOT CARD] Duplicate clicked', { botId: bot.id, botName: bot.name });
    setIsDuplicating(true);

    try {
      // ✅ SIMPLE FIX: Just clone the clicked bot!
      // Database RPC function (f_bots_clone_bot_v2) handles hierarchical naming automatically
      // Examples:
      //   Click "Best mano 3" → Creates "Best mano 3 1"
      //   Click "Best mano 3" again → Creates "Best mano 3 2"
      //   Click "Best mano 3 2" → Creates "Best mano 3 2 1"

      console.log('[BOT CARD] Cloning bot directly (DB handles naming):', {
        clickedBot: bot.name,
        clickedBotId: bot.id
      });

      // Clone the clicked bot (DB generates hierarchical name)
      const cloned = await cloneBotV2(bot.id, user.id);
      const newBotId = cloned.id || cloned.dataId;
      const newName = cloned.name || cloned.dataName || 'Bot';

      console.log('[BOT CARD] Clone successful', {
        sourceBot: bot.name,
        newBot: newName,
        newBotId: newBotId,
        counter: cloned.counter,
        baseName: cloned.base_name
      });

      // Show success message immediately
      toast.success(`Bot duplicated as "${newName}"`);

      // ✅ CRITICAL: Refresh bots list to show new bot immediately
      console.log('[BOT CARD] Refreshing bots list...');
      await refreshBots();
      console.log('[BOT CARD] Bots list refreshed successfully');

      // Navigate to new bot's settings page
      console.log('[BOT CARD] Navigating to new bot settings:', `/bots/${newBotId}/edit`);
      navigate(`/bots/${newBotId}/edit`, { state: { prefillBot: cloned } });
    } catch (error: any) {
      console.error('[BOT CARD] Clone failed', error);
      toast.error(error?.message || 'Failed to duplicate bot');
    } finally {
      setIsDuplicating(false);
    }
  };

  const hashString = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <>
      <motion.div 
        layout 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        exit={{ opacity: 0, scale: 0.95 }}
        className={cn(
          "col-span-1 bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border transition-all group relative",
          isSelected ? "border-blue-500 dark:border-blue-400 shadow-blue-500/20" : "border-gray-200 dark:border-gray-700 hover:shadow-xl"
        )}
        onClick={handleSelect} 
        role="button" 
        tabIndex={0}
      >
        <div className="relative h-48">
          <div className={`absolute inset-0 bg-gradient-to-br ${getGradient()} animate-gradient bg-[length:200%_200%]`}>
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>
          </div>

          <div className="absolute inset-0 p-4 flex flex-col min-h-full">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                {/* Bot status indicator */}
                {bot?.is_active !== undefined && (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <span className={`inline-block h-3 w-3 rounded-full ${
                        !bot.isConnected ? 'bg-orange-500' :
                        bot.is_running === true ? 'bg-green-500' : 'bg-gray-500'
                      }`}></span>
                      {bot.is_running && bot.isConnected && (
                        <span className="absolute inset-0 rounded-full animate-ping bg-green-400 opacity-75"></span>
                      )}
                    </div>
                    <span className="text-xs text-white/90 font-medium">
                      {!bot.isConnected ? 'Error' : bot.is_running ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                )}
                
                {/* Backtest origin indicator */}
                {isFromBacktest && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-white/80 bg-white/20 px-2 py-0.5 rounded-full backdrop-blur-sm">
                      📈 From Backtest
                    </span>
                  </div>
                )}
              </div>
              {onDelete && (
                <div className="relative">
                  <button onClick={handleDropdownToggle} className="p-1 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors backdrop-blur-sm">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  <AnimatePresence>
                    {showDropdown && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        className="absolute right-0 top-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10 min-w-[140px]"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDropdown(false);
                            navigate(`/sell-bot?botId=${bot.id}`);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
                        >
                          <CreditCard className="h-4 w-4" />
                          Sell Bot
                        </button>
                        <button onClick={handleDeleteClick} className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2">
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col justify-center">
              {/* My Bots: Strategy and Profit in top row */}
              {isMyBots && (
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-white/90 truncate">
                    {strategyLabel}
                  </div>
                  <div className={cn(
                    "text-sm font-bold tabular-nums",
                    profitPct >= 0 ? "text-green-300" : "text-red-300"
                  )}>
                    {formatPct(profitPct)}
                  </div>
                </div>
              )}
              
              <h3 className="text-xl font-bold text-white drop-shadow-lg truncate">
                {bot.title ?? 'Unnamed Bot'}
              </h3>
              
              {/* Show strategy and status for non-My Bots variants */}
              {!isMyBots && (
                <div className="mt-2 space-y-1">
                  {bot.strategy && (
                    <p className="text-sm text-white/90 truncate">{formatValue(bot.strategy)}</p>
                  )}
                  {bot.status && (
                    <p className="text-xs text-white/80 bg-white/20 px-2 py-0.5 rounded-full inline-block">
                      {bot.status}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex-none space-y-2">
              {/* Show profit and trades for non-My Bots variants only */}
              {!isMyBots && (
                <div className="flex justify-between items-center">
                  {typeof bot.profit === 'number' && (
                    <p className={`text-lg font-bold ${bot.profit >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                      {bot.profit >= 0 ? '+' : ''}{bot.profit.toFixed(2)}%
                    </p>
                  )}
                  {typeof bot.trades === 'number' && (
                    <p className="text-sm text-white/90">
                      Trades: {formatValue(bot.trades)}
                    </p>
                  )}
                </div>
              )}
              
              {bot.createdAt && (
                <p className="text-xs text-white/80">
                  Created: {formatLocalTime(bot.createdAt)}
                </p>
              )}
              
              {/* My Bots: 2 buttons + Activate/Deactivate + Duplicate in same row */}
              {isMyBots ? (
                <div className="space-y-1">
                  <div className="grid grid-cols-2 gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/bot-stats?botId=${bot.id}`);
                      }}
                      className="flex items-center justify-center px-2 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-xs font-medium backdrop-blur-sm"
                    >
                      Bot Stats
                    </button>
                    {onClick && (
                      <Link
                        to={`/bots/${bot.id}/edit`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center gap-1 px-2 py-2 bg-black/20 hover:bg-black/30 text-white rounded-lg transition-colors text-xs font-medium backdrop-blur-sm"
                      >
                        <Edit className="h-3 w-3" />
                        Edit
                      </Link>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <button
                      onClick={handleToggleActive}
                      disabled={isToggling}
                      className={cn(
                        "flex items-center justify-center px-2 py-2 rounded-lg transition-colors text-xs font-medium backdrop-blur-sm",
                        bot.is_active
                          ? "bg-red-500/80 hover:bg-red-600/80 text-white"
                          : "bg-green-500/80 hover:bg-green-600/80 text-white",
                        isToggling && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {isToggling ? 'Processing...' : (bot.is_active ? 'Deactivate' : 'Activate')}
                    </button>
                    <button
                      onClick={handleDuplicate}
                      disabled={isDuplicating}
                      title="Create a copy of this bot"
                      className="flex items-center justify-center gap-1 px-2 py-2 bg-blue-500/80 hover:bg-blue-600/80 text-white rounded-lg transition-colors text-xs font-medium backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Copy className="h-3 w-3" />
                      {isDuplicating ? 'Duplicating...' : 'Duplicate'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Original layout for other variants */
                <div className="grid grid-cols-2 gap-2">
                  {bot.id ? (
                    <Link to={`/bot/${bot.id}`} className="flex items-center justify-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm font-medium backdrop-blur-sm" onClick={(e) => e.stopPropagation()}>
                      <LineChart className="w-4 h-4" />
                      Details
                    </Link>
                  ) : (
                    <button className="flex items-center justify-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm font-medium backdrop-blur-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect();
                      }}>
                      <Bot className="w-4 w-4" />
                      Select
                    </button>
                  )}
                  {onClick && (
                    <Link to={`/bots/${bot.id}/edit`} onClick={(e) => {
                      e.stopPropagation();
                    }} className="flex items-center justify-center gap-2 px-3 py-2 bg-black/20 hover:bg-black/30 text-white rounded-lg font-medium transition-colors text-sm font-medium backdrop-blur-sm">
                      <Edit className="h-4 w-4" />
                      Edit
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showDeleteModal && (
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleConfirmDelete}
            botName={bot.title ?? 'Unnamed Bot'}
            isDeleting={isDeleting}
          />
        )}
      </AnimatePresence>

      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center">
                <Bot className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Deactivate Bot
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Bot will be removed from Active Bots
                </p>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              <p className="text-gray-600 dark:text-gray-300">
                Are you sure you want to deactivate <strong>"{bot.title ?? 'Unnamed Bot'}"</strong>?
              </p>
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                <p className="text-sm text-orange-800 dark:text-orange-300 font-medium mb-2">
                  This will:
                </p>
                <ul className="text-sm text-orange-700 dark:text-orange-400 space-y-1 ml-4">
                  <li>• Remove bot from Active Bots category</li>
                  <li>• Close all open {bot.deployment_target === 'live' ? 'Live' : 'Paper'} trading positions</li>
                  <li>• Cancel all pending entry orders</li>
                  <li>• Delete all {bot.deployment_target === 'live' ? 'Live' : 'Paper'} trades from Active Bots</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeactivateModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeactivate}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
              >
                Deactivate Bot
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default BotCard;